// Display-only helpers for the dialer widget (CU-869fmc59c). Dependency-free so Jest loads them
// without the widget. Nothing here changes a value that is dialled, saved or sent.
import { formatUkPhone } from '../../utils/ukPhone';

// Phone numbers inside a sentence ("Call +441134960129") shown the way staff say them
// ("Call 0113 496 0129"). Anything that isn't a UK number is left as written.
export const ukNumbersInText = (text) => {
    if (typeof text !== 'string') return text;
    return text.replace(/\+?\d[\d ()-]{7,}\d/g, (match) => formatUkPhone(match));
};

// What the dialer's "isn't set up for you yet" notice offers each person. One place decides, so a
// button never shows for someone who can't use it:
//   chooseDesk  a desk caller whose desk isn't chosen yet: the existing desk picker
//   setUpDesks  practice admins only: Integrations > Desks
//   askAdmin    everyone else: say so (and the Ask an admin button when there is a call)
// A role without calling (`reason` no_permission / no_twilio_permission, CU-869fn2wd3) isn't fixed by
// setting desks up, so that button is left out for it.
const ROLE_REASONS = new Set(['no_permission', 'no_twilio_permission']);
export const noRouteView = ({ canCallElevate = false, isVoiceAdmin = false, reason = '' } = {}) => ({
    chooseDesk: Boolean(canCallElevate),
    setUpDesks: !canCallElevate && Boolean(isVoiceAdmin) && !ROLE_REASONS.has(reason),
    askAdmin: !canCallElevate && !isVoiceAdmin,
});

// The real reason calling isn't possible (the dialer endpoint's `reason`, CU-869fn2wd3), with the
// next step for this person. An older backend sends no reason: keep the general text.
export const NO_ROUTE_GENERAL = "Calling from the CRM isn't set up for you yet. You can dial on your handset meanwhile.";
// Feature switches are DentalSEM's (every permissions write is superuser-only, usePhoneFeatures.js),
// so a practice admin is told to ask DentalSEM and everyone else to ask an admin.
export const noRouteReason = (reason, { isVoiceAdmin = false } = {}) => {
    const ask = isVoiceAdmin ? 'Ask DentalSEM support' : 'Ask an admin';
    const meanwhile = 'You can dial on your handset meanwhile.';
    if (reason === 'no_permission') {
        return `Your role can't make calls from the CRM. ${ask} to turn on "Make calls from CRM" for your role. ${meanwhile}`;
    }
    if (reason === 'no_twilio_permission') {
        return `Your role can't call through the practice's Twilio numbers. ${ask} to turn on Twilio voice calling for your role. ${meanwhile}`;
    }
    if (reason === 'calling_off') {
        const next = isVoiceAdmin ? 'Connect one to turn calling on.' : 'Ask an admin to connect it.';
        return `Calling from the CRM is switched off: this practice has no phone system connected. ${next} ${meanwhile}`;
    }
    return NO_ROUTE_GENERAL;
};

